import { CollectionPreferencesProps } from '@amzn/awsui-components-console';
import {
  defaultTablePreferences,
  loadTablePreferences,
  normaliseTablePreferences,
  pageSizeOf,
  sanitiseContentDisplay as sanitiseAgainstConfig,
  saveTablePreferences,
  type TablePreferencesConfig,
  type TablePreferencesValue,
} from 'src/common/ui/TablePreferences';
import { SORT_FIELD_LABELS } from '../sortState';
/*
 * TYPE-ONLY import, and it must stay that way: a value import from `SearchPage`
 * closes a require cycle (`SearchPage` → `SearchResultsTable` → here) and leaves
 * this module's exports undefined at eval time. Hence the literal mode keys
 * below, still checked against `SearchMode` by the `Record`.
 */
import type { SearchMode } from '../SearchPage';

/**
 * The search table's column sets and its binding to the shared core; only what is
 * search-specific lives here — one column set and one key per mode.
 */

/**
 * Offered page sizes, and the allowlist for values read back from storage. Search-owned,
 * not the shared default: only search's size drives a backend query bounded by
 * `MAX_PAGE_SIZE`, pinned by a test. See `searchTablePreferences.test.ts`.
 */
export const PAGE_SIZE_OPTIONS: readonly number[] = [25, 50, 75];

export const DEFAULT_PAGE_SIZE = 25;

/**
 * The trailing Details/actions column, in both modes. It is `alwaysVisible` in
 * the options below and pinned last by the config, because the SCSS width pin
 * targets whichever header is rightmost (`thead th[data-rightmost]`) — hiding it
 * or moving it would squash whatever column ended up there instead.
 */
const ACTIONS_COLUMN_ID = 'actions';

/** Label the actions column shows in the preferences list; its header is blank. */
const ACTIONS_COLUMN_LABEL = 'Details';

/**
 * Column options per mode; the order is the default column order and mirrors the
 * column-definition arrays in `SearchResultsTable`. The two modes' ids only
 * partly overlap, which is why they persist under separate keys and are
 * reconciled against the options of the mode being read.
 */
const COLUMN_OPTIONS: Record<
  SearchMode,
  readonly CollectionPreferencesProps.ContentDisplayOption[]
> = {
  SOURCE: [
    { id: 'stringId', label: SORT_FIELD_LABELS.STRING_ID },
    { id: 'group', label: SORT_FIELD_LABELS.STRING_GROUP },
    { id: 'namespace', label: SORT_FIELD_LABELS.NAMESPACE },
    { id: 'sourceContent', label: 'Source text' },
    { id: 'status', label: SORT_FIELD_LABELS.STATUS },
    { id: ACTIONS_COLUMN_ID, label: ACTIONS_COLUMN_LABEL, alwaysVisible: true },
  ],
  TRANSLATION: [
    { id: 'stringId', label: SORT_FIELD_LABELS.STRING_ID },
    { id: 'group', label: SORT_FIELD_LABELS.STRING_GROUP },
    { id: 'namespace', label: SORT_FIELD_LABELS.NAMESPACE },
    { id: 'localizedText', label: 'Localized text' },
    { id: 'locale', label: 'Locale' },
    { id: ACTIONS_COLUMN_ID, label: ACTIONS_COLUMN_LABEL, alwaysVisible: true },
  ],
};

/** The shared config for a mode. The key is verbatim: changing it discards preferences. */
export function searchTablePreferencesConfig(
  mode: SearchMode,
): TablePreferencesConfig {
  return {
    storageKey: `LangHub-searchTablePreferences-${mode}`,
    columnOptions: COLUMN_OPTIONS[mode],
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    defaultPageSize: DEFAULT_PAGE_SIZE,
    pageSizeLabel: (pageSize) => `${pageSize} results`,
    pinnedLastColumnId: ACTIONS_COLUMN_ID,
  };
}

/** The preferences list for a mode, in default column order. */
export function columnOptionsForMode(
  mode: SearchMode,
): readonly CollectionPreferencesProps.ContentDisplayOption[] {
  return COLUMN_OPTIONS[mode];
}

/** `pageSize` is required where the shared type leaves it optional: search always pages. */
export interface SearchTablePreferences extends Omit<
  TablePreferencesValue,
  'pageSize'
> {
  readonly pageSize: number;
}

/** The defaults, and the fallback for anything unreadable in storage. */
export function defaultSearchTablePreferences(
  mode: SearchMode,
): SearchTablePreferences {
  return withRequiredPageSize(
    defaultTablePreferences(searchTablePreferencesConfig(mode)),
  );
}

/** Narrows the optional `pageSize`; the fallback is a type guard, not behaviour. */
function withRequiredPageSize(
  value: TablePreferencesValue,
): SearchTablePreferences {
  return { ...value, pageSize: pageSizeOf(value, DEFAULT_PAGE_SIZE) };
}

/** See the shared `sanitiseContentDisplay`; this binds it to a mode. */
export function sanitiseContentDisplay(
  stored: unknown,
  mode: SearchMode,
): CollectionPreferencesProps.ContentDisplayColumn[] {
  return sanitiseAgainstConfig(stored, searchTablePreferencesConfig(mode));
}

/** Normalises a Cloudscape confirm payload for a mode. */
export function normaliseSearchTablePreferences(
  preferences: CollectionPreferencesProps.Preferences,
  mode: SearchMode,
): SearchTablePreferences {
  return withRequiredPageSize(
    normaliseTablePreferences(preferences, searchTablePreferencesConfig(mode)),
  );
}

/** Reads a mode's persisted preferences, falling back to the defaults. */
export function loadSearchTablePreferences(
  mode: SearchMode,
): SearchTablePreferences {
  return withRequiredPageSize(
    loadTablePreferences(searchTablePreferencesConfig(mode)),
  );
}

/** Persists a mode's preferences. */
export function saveSearchTablePreferences(
  mode: SearchMode,
  preferences: SearchTablePreferences,
): void {
  saveTablePreferences(searchTablePreferencesConfig(mode), preferences);
}

export { COLUMN_DISPLAY_DND_LABELS } from 'src/common/ui/TablePreferences';
